import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, Spinner, Alert, OverlayTrigger, Tooltip, Nav, Tab, Modal } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBrain, 
  faEdit, 
  faSave, 
  faFileAlt, 
  faCheckCircle, 
  faExclamationTriangle,
  faRobot,
  faLightbulb,
  faClipboardCheck,
  faClock,
  faGavel,
  faUsers,
  faBuilding,
  faKey,
  faEye,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faFilter,
  faDownload,
  faExternalLinkAlt,
  faUser,
  faListCheck,
  faPlus,
  faInfoCircle,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';
import KairosLogo from 'assets/img/logos/KairosIQ-LogoOnly.png';
import DocumentTimingTemplates from './DocumentTimingTemplates';
import DocumentComplianceRulesSimplified from './DocumentComplianceRulesSimplified';
import './AIDocumentReview.css';

export interface AIAnalysis {
  summary?: string;
  document_purpose?: string;
  filename?: string;
  document_type?: string;
  enriched_at?: string;
  chatbot_qa?: Array<{
    question: string;
    answer: string;
  }>;
  key_entities?: any;
  key_information?: {
    parties_involved?: string[];
    effective_date?: string;
    critical_terms?: string[];
    document_purpose?: string;
    filing_deadline?: string;
  };
  usage_context?: {
    when_required?: {
      always_required?: boolean;
      timing_conditions?: Array<{
        trigger?: string;
        deadline?: string;
        priority?: string;
      }>;
      property_conditions?: string[];
      transaction_conditions?: string[];
      transaction_stages?: string[];
    };
    common_scenarios?: Array<{
      scenario: string;
      timing: string;
      required_documents: string[];
    }>;
    related_documents?: Array<{
      document_type: string;
      relationship: string;
      condition: string;
    }>;
    smart_checklist_rules?: Array<{
      rule_type: string;
      condition_field: string;
      condition_operator: string;
      condition_value: string;
      then_required: boolean;
      explanation: string;
      priority: string;
    }>;
    who_needs?: {
      signing_parties?: string[];
      reviewing_parties?: string[];
    };
    why_important?: {
      legal_requirements?: string[];
      protections_provided?: string[];
      risks_if_missing?: string[];
    };
  };
  search_keywords?: string[];
  analysis_details?: {
    summary?: string;
    red_flags?: string[];
    chatbot_qa?: Array<{
      question: string;
      answer: string;
    }>;
  };
  compliance_requirements?: {
    legal_references?: string[];
    casa_compliance?: boolean;
    state_specific?: boolean;
  };
  document_classification?: {
    primary_category?: string;
    subcategory?: string;
    transaction_types?: string[];
    required_frequency?: string;
  };
  ai_confidence?: number;
  last_updated?: string;
}

interface DocumentData {
  id: string;
  document_type: string;
  filename: string;
  file_url?: string;
  s3_url?: string;
  s3_key?: string;
  ai_analysis?: AIAnalysis | string;
  created_at: string;
  updated_at: string;
  template_category?: string;
  is_active?: boolean;
  file_size?: number;
  file_size_formatted?: string;
}

interface AIDocumentReviewProps {
  brokerageId?: string;
  brokerageName?: string;
}

const AIDocumentReview: React.FC<AIDocumentReviewProps> = ({ brokerageId, brokerageName }) => {
  // Use the same pattern as BrokerDocumentManager
  const effectiveBrokerageId = brokerageId || localStorage.getItem('brokerageId') || '';
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract sub-tab from URL
  const getTabFromPath = () => {
    const pathParts = location.pathname.split('/');
    const aiReviewIndex = pathParts.indexOf('ai-review');
    const subTab = pathParts[aiReviewIndex + 1];
    if (subTab === 'templates') return 'templates';
    if (subTab === 'rules') return 'rules';
    return 'uploaded';
  };
  
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);
  const [editedAnalysis, setEditedAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [filterTransactionType, setFilterTransactionType] = useState('all');
  const [activeTab, setActiveTab] = useState<'uploaded' | 'templates' | 'rules'>(getTabFromPath());
  const [templates, setTemplates] = useState<DocumentData[]>([]);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('summary');
  const [showTimingTemplates, setShowTimingTemplates] = useState(false);

  // Update tab when URL changes
  useEffect(() => {
    const newTab = getTabFromPath();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname]);
  
  useEffect(() => {
    console.log('🤖 AIDocumentReview initialized with:', { 
      brokerageId, 
      effectiveBrokerageId,
      brokerageIdType: typeof brokerageId
    });
    if (activeTab === 'uploaded') {
      fetchDocuments();
    } else {
      fetchTemplates();
    }
  }, [effectiveBrokerageId, activeTab]);

  const fetchTemplates = async () => {
    if (!effectiveBrokerageId) {
      console.log('🤖 AIDocumentReview: Skipping fetch - no brokerageId');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('📄 Fetching document templates...');
      
      // Fetch documents that are marked as placeholders/templates
      const response = await axiosInstance.get('/documents/my-documents', {
        params: {
          owner_type: 'brokerage',
          brokerage_id: effectiveBrokerageId,
          is_placeholder: true // This will be our flag for templates
        }
      });
      
      if (response.data.success) {
        console.log('📄 Found document templates:', response.data.documents.length);
        setTemplates(response.data.documents || []);
        
        if (response.data.documents.length > 0 && !isCreatingTemplate) {
          selectDocument(response.data.documents[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      toast.error('Failed to load templates');
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!effectiveBrokerageId) {
      console.log('🤖 AIDocumentReview: Skipping fetch - no brokerageId');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('📄 Fetching documents with params:', {
        owner_type: 'brokerage',
        brokerage_id: effectiveBrokerageId
      });
      
      // Use the same endpoint as BrokerDocumentManager with axiosInstance
      const response = await axiosInstance.get('/documents/my-documents', {
        params: {
          owner_type: 'brokerage',
          brokerage_id: effectiveBrokerageId
        }
      });
      
      if (response.data.success) {
        console.log('📄 Found brokerage documents:', response.data.documents.length);
        
        const allDocs = response.data.documents || [];
        const documentsWithAI = allDocs.filter((doc: DocumentData) => doc.ai_analysis);
        
        console.log(`📊 Found ${documentsWithAI.length} documents with AI analysis out of ${allDocs.length} total`);
        
        // Show all documents for now so user can see what's available
        setDocuments(allDocs);
        
        if (allDocs.length > 0) {
          selectDocument(allDocs[0]);
        }
        
        // If no brokerage documents found, also try to fetch user's personal documents
        if (allDocs.length === 0) {
          console.log('📄 No brokerage documents found, fetching user documents...');
          
          const userDocsResponse = await axiosInstance.get('/documents/my-documents', {
            params: {
              // No parameters = get user's documents
            }
          });
          
          if (userDocsResponse.data.success && userDocsResponse.data.documents.length > 0) {
            console.log('📄 Found user documents:', userDocsResponse.data.documents.length);
            setDocuments(userDocsResponse.data.documents);
            selectDocument(userDocsResponse.data.documents[0]);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error('Failed to load documents');
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const selectDocument = (doc: DocumentData) => {
    setSelectedDocument(doc);
    // Parse AI analysis if it's a string
    let analysis: AIAnalysis = {};
    if (doc.ai_analysis) {
      try {
        analysis = typeof doc.ai_analysis === 'string' 
          ? JSON.parse(doc.ai_analysis) 
          : doc.ai_analysis;
      } catch (e) {
        console.error('Error parsing AI analysis:', e);
        analysis = {};
      }
    }
    setEditedAnalysis(analysis);
    setSuccess(null);
    setError(null);
  };

  const handleAnalysisChange = (path: string, value: any) => {
    if (!editedAnalysis) return;

    const newAnalysis = { ...editedAnalysis };
    const pathParts = path.split('.');
    let current: any = newAnalysis;

    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }

    current[pathParts[pathParts.length - 1]] = value;
    setEditedAnalysis(newAnalysis);
  };

  // Helper function for comma-separated inputs
  const handleCommaSeparatedInput = (path: string, value: string, parseOnChange: boolean = false) => {
    if (parseOnChange) {
      // Parse immediately (for fields where we want immediate parsing)
      const values = value.split(',').map(s => s.trim()).filter(s => s);
      handleAnalysisChange(path, values);
    } else {
      // For better UX, we'll parse on blur instead of onChange
      // This allows users to type commas and spaces naturally
      handleAnalysisChange(path, value);
    }
  };

  const parseCommaSeparatedOnBlur = (path: string, value: string) => {
    const values = value.split(',').map(s => s.trim()).filter(s => s);
    handleAnalysisChange(path, values);
  };

  const saveAnalysis = async () => {
    if (!editedAnalysis) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (isCreatingTemplate) {
        // Creating a new template
        console.log('Creating new document template:', {
          analysis: editedAnalysis
        });
        
        const response = await axiosInstance.post('/documents/create-template', {
          filename: editedAnalysis.filename || 'New Document Template',
          document_type: editedAnalysis.document_type || 'template',
          owner_type: 'brokerage',
          brokerage_id: effectiveBrokerageId,
          is_placeholder: true,
          ai_analysis: editedAnalysis
        });

        if (response.data.success) {
          toast.success('Document template created successfully!');
          setSuccess('Document template created successfully!');
          setIsCreatingTemplate(false);
          fetchTemplates(); // Refresh the template list
        }
      } else if (selectedDocument) {
        // Updating existing document/template
        console.log('Saving AI analysis:', {
          documentId: selectedDocument.id,
          analysis: editedAnalysis
        });
        
        const response = await axiosInstance.put(`/documents/${selectedDocument.id}/ai-analysis`, {
          ai_analysis: editedAnalysis
        });

        if (response.data.success) {
          toast.success('AI analysis updated successfully!');
          setSuccess('AI analysis updated successfully!');
          
          // Update the document in the appropriate list
          if (activeTab === 'uploaded') {
            setDocuments(prev => prev.map(doc => 
              doc.id === selectedDocument.id 
                ? { ...doc, ai_analysis: editedAnalysis }
                : doc
            ));
          } else {
            setTemplates(prev => prev.map(doc => 
              doc.id === selectedDocument.id 
                ? { ...doc, ai_analysis: editedAnalysis }
                : doc
            ));
          }
          
          // Update the selected document
          setSelectedDocument(prev => prev ? { ...prev, ai_analysis: editedAnalysis } : null);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save AI analysis';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error saving AI analysis:', err);
    } finally {
      setSaving(false);
    }
  };

  const getConfidenceColor = (confidence?: number) => {
    if (!confidence) return 'secondary';
    if (confidence >= 90) return 'success';
    if (confidence >= 70) return 'warning';
    return 'danger';
  };

  const getDocumentIcon = (type?: string) => {
    if (!type) return faFileAlt;
    
    const iconMap: { [key: string]: any } = {
      'buyer_representation': faUsers,
      'purchase_agreement': faFileAlt,
      'listing_agreement': faBuilding,
      'disclosure': faExclamationTriangle,
      'legal': faGavel,
      'financial': faClock
    };
    
    const lowerType = type.toLowerCase();
    return iconMap[lowerType] || faFileAlt;
  };

  const currentDocuments = activeTab === 'uploaded' ? documents : templates;
  
  // Calculate counts for filters
  const stageCounts = {
    all: currentDocuments.length,
    pre_contract: 0,
    showing: 0,
    under_contract: 0,
    closing: 0,
    cancelled: 0
  };
  
  const classificationCounts = {
    all: currentDocuments.length,
    required: 0,
    conditional: 0,
    optional: 0,
    supplemental: 0
  };
  
  const transactionTypeCounts = {
    all: currentDocuments.length,
    purchase: 0,
    listing: 0,
    rental: 0,
    referral: 0,
    'land contract': 0,
    commercial: 0
  };
  
  currentDocuments.forEach(doc => {
    let analysis: AIAnalysis = {};
    if (doc.ai_analysis) {
      try {
        analysis = typeof doc.ai_analysis === 'string' 
          ? JSON.parse(doc.ai_analysis) 
          : doc.ai_analysis;
      } catch (e) {
        analysis = {};
      }
    }
    
    // Count stages
    const stages = analysis.usage_context?.when_required?.transaction_stages || [];
    stages.forEach((stage: string) => {
      if (stageCounts[stage as keyof typeof stageCounts] !== undefined) {
        stageCounts[stage as keyof typeof stageCounts]++;
      }
    });
    
    // Count classifications
    const classification = analysis.document_classification?.primary_category?.toLowerCase();
    if (classification && classificationCounts[classification as keyof typeof classificationCounts] !== undefined) {
      classificationCounts[classification as keyof typeof classificationCounts]++;
    }
    
    // Count transaction types
    const transactionTypes = analysis.document_classification?.transaction_types || [];
    transactionTypes.forEach((type: string) => {
      const typeLower = type.toLowerCase();
      if (transactionTypeCounts[typeLower as keyof typeof transactionTypeCounts] !== undefined) {
        transactionTypeCounts[typeLower as keyof typeof transactionTypeCounts]++;
      }
    });
  });
  
  const filteredDocuments = currentDocuments.filter(doc => {
    // Search filter
    const matchesSearch = doc.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.document_type.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Parse AI analysis if it's a string
    let analysis: AIAnalysis = {};
    if (doc.ai_analysis) {
      try {
        analysis = typeof doc.ai_analysis === 'string' 
          ? JSON.parse(doc.ai_analysis) 
          : doc.ai_analysis;
      } catch (e) {
        analysis = {};
      }
    }
    
    // Stage filter
    const matchesStage = filterStage === 'all' || 
      (analysis.usage_context?.when_required?.transaction_stages?.includes(filterStage));
    
    // Classification filter  
    const matchesCategory = filterCategory === 'all' || 
      (analysis.document_classification?.primary_category?.toLowerCase() === filterCategory.toLowerCase());
    
    // Transaction type filter
    const matchesTransactionType = filterTransactionType === 'all' || 
      (analysis.document_classification?.transaction_types?.some((type: string) => 
        type.toLowerCase() === filterTransactionType.toLowerCase()
      ));
    
    return matchesSearch && matchesStage && matchesCategory && matchesTransactionType;
  });

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" style={{ color: '#00d4ff' }} />
        <p className="mt-3 text-muted">Loading AI document analysis...</p>
      </div>
    );
  }

  return (
    <Container fluid className="ai-document-review p-0">
      {/* Tab Navigation */}
      <Nav variant="tabs" className="border-bottom px-3 pt-3">
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'uploaded'} 
            onClick={() => {
              setActiveTab('uploaded');
              setIsCreatingTemplate(false);
              setSelectedDocument(null);
              setEditedAnalysis(null);
              navigate('/broker-intelligence/ai-review');
            }}
          >
            <FontAwesomeIcon icon={faFileAlt} /> Uploaded Documents
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'templates'} 
            onClick={() => {
              setActiveTab('templates');
              setSelectedDocument(null);
              setEditedAnalysis(null);
              navigate('/broker-intelligence/ai-review/templates');
            }}
          >
            <FontAwesomeIcon icon={faListCheck} /> Document Templates
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'rules'} 
            onClick={() => {
              setActiveTab('rules');
              setSelectedDocument(null);
              setEditedAnalysis(null);
              navigate('/broker-intelligence/ai-review/rules');
            }}
          >
            <FontAwesomeIcon icon={faCog} /> Compliance Rules
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      {activeTab !== 'rules' && (
      <Row className="g-0 h-100">
        {/* Document List Sidebar */}
        <Col md={3} className="document-sidebar border-end">
          <div className="p-3">
            <div className="mb-3">
              <Form.Control
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ai-search-input"
              />
            </div>
            <div className="mb-2">
              <Form.Label size="sm" className="text-muted mb-1">Transaction Stage</Form.Label>
              <Form.Select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="ai-filter-select"
                size="sm"
              >
                <option value="all">All Stages ({stageCounts.all})</option>
                <option value="pre_contract">Pre-Contract ({stageCounts.pre_contract})</option>
                <option value="showing">Showing ({stageCounts.showing})</option>
                <option value="under_contract">Under Contract ({stageCounts.under_contract})</option>
                <option value="closing">Closing ({stageCounts.closing})</option>
                <option value="cancelled">Cancelled ({stageCounts.cancelled || 0})</option>
              </Form.Select>
            </div>
            <div className="mb-3">
              <Form.Label size="sm" className="text-muted mb-1">Classification</Form.Label>
              <Form.Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="ai-filter-select"
                size="sm"
              >
                <option value="all">All Classifications ({classificationCounts.all})</option>
                <option value="required">Required ({classificationCounts.required})</option>
                <option value="conditional">Conditional ({classificationCounts.conditional})</option>
                <option value="optional">Optional ({classificationCounts.optional})</option>
                <option value="supplemental">Supplemental ({classificationCounts.supplemental})</option>
              </Form.Select>
            </div>
            <div className="mb-3">
              <Form.Label size="sm" className="text-muted mb-1">Transaction Type</Form.Label>
              <Form.Select
                value={filterTransactionType}
                onChange={(e) => setFilterTransactionType(e.target.value)}
                className="ai-filter-select"
                size="sm"
              >
                <option value="all">All Types ({transactionTypeCounts.all})</option>
                <option value="purchase">Purchase ({transactionTypeCounts.purchase})</option>
                <option value="listing">Listing ({transactionTypeCounts.listing})</option>
                <option value="rental">Rental ({transactionTypeCounts.rental})</option>
                <option value="referral">Referral ({transactionTypeCounts.referral})</option>
                <option value="land contract">Land Contract ({transactionTypeCounts['land contract']})</option>
                <option value="commercial">Commercial ({transactionTypeCounts.commercial})</option>
              </Form.Select>
            </div>
            <div className="document-list">
              {/* For templates tab, show create new button */}
              {activeTab === 'templates' && (
                <Button 
                  variant="outline-primary" 
                  className="w-100 mb-3"
                  onClick={() => {
                    setIsCreatingTemplate(true);
                    setSelectedDocument(null);
                    setEditedAnalysis({
                      summary: '',
                      document_purpose: '',
                      filename: '',
                      document_type: 'template',
                      key_information: {},
                      usage_context: {
                        when_required: {
                          transaction_stages: []
                        }
                      },
                      document_classification: {
                        primary_category: 'Required'
                      }
                    });
                    setActiveTabKey('summary'); // Switch to Summary tab
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create New Template
                </Button>
              )}
              
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map(doc => (
                  <Card
                    key={doc.id}
                    className={`document-item mb-2 ${selectedDocument?.id === doc.id ? 'selected' : ''}`}
                    onClick={() => selectDocument(doc)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex align-items-start">
                        <FontAwesomeIcon 
                          icon={getDocumentIcon(doc.document_type)} 
                          className="me-2 mt-1 text-primary"
                        />
                        <div className="flex-grow-1">
                          <h6 
                            className="mb-1" 
                            title={doc.filename}
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: '1.2',
                              minHeight: '2.4em' // Ensures consistent height for 2 lines
                            }}
                          >
                            {doc.filename}
                          </h6>
                          <div className="d-flex align-items-center justify-content-between">
                            <small className="text-muted">
                              {doc.document_type.replace(/_/g, ' ')}
                            </small>
                            {doc.file_size_formatted && (
                              <small className="text-muted">
                                {doc.file_size_formatted}
                              </small>
                            )}
                          </div>
                          {doc.ai_analysis && (
                            <div className="mt-1 d-flex gap-1 flex-wrap">
                              <Badge 
                                bg={getConfidenceColor(
                                  typeof doc.ai_analysis === 'object' 
                                    ? doc.ai_analysis.ai_confidence 
                                    : undefined
                                )}
                                className="d-inline-flex align-items-center"
                                style={{ fontSize: '10px', whiteSpace: 'nowrap' }}
                              >
                                <img 
                                  src={KairosLogo} 
                                  alt="AI" 
                                  className="me-1"
                                  style={{ 
                                    width: '12px', 
                                    height: '12px',
                                    verticalAlign: 'middle'
                                  }}
                                />
                                {typeof doc.ai_analysis === 'object' && doc.ai_analysis.ai_confidence 
                                  ? `${doc.ai_analysis.ai_confidence}%` 
                                  : 'AI Analyzed'}
                              </Badge>
                              {doc.s3_url && (
                                <Badge 
                                  bg="success"
                                  className="d-inline-flex align-items-center"
                                  style={{ fontSize: '10px', whiteSpace: 'nowrap' }}
                                >
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                  Available
                                </Badge>
                              )}
                              {/* Display transaction types */}
                              {(() => {
                                let analysis: AIAnalysis = {};
                                if (doc.ai_analysis) {
                                  try {
                                    analysis = typeof doc.ai_analysis === 'string' 
                                      ? JSON.parse(doc.ai_analysis) 
                                      : doc.ai_analysis;
                                  } catch (e) {
                                    analysis = {};
                                  }
                                }
                                
                                if (analysis.document_classification?.transaction_types?.length > 0) {
                                  return analysis.document_classification.transaction_types.map((type: string) => (
                                    <Badge 
                                      key={type} 
                                      bg="info"
                                      className="d-inline-flex align-items-center"
                                      style={{ fontSize: '10px', whiteSpace: 'nowrap' }}
                                    >
                                      {type}
                                    </Badge>
                                  ));
                                }
                                return null;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))
              ) : (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faSearch} size="2x" className="text-muted mb-2" />
                  <p className="text-muted mb-0">
                    {activeTab === 'uploaded' 
                      ? (searchTerm ? 'No documents match your search' : 'No documents found')
                      : (searchTerm ? 'No matching templates found' : 'No document templates created yet')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Col>

        {/* Document Analysis Editor */}
        <Col md={9} className="document-editor">
          {(selectedDocument || isCreatingTemplate) ? (
            <div className="h-100 d-flex flex-column">
              {/* Header */}
              <div className="editor-header p-4 border-bottom">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h4 className="mb-2">
                      {isCreatingTemplate ? (
                        <>
                          <FontAwesomeIcon icon={faPlus} className="me-2 text-primary" />
                          Create Document Template
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={getDocumentIcon(selectedDocument?.document_type || '')} className="me-2 text-primary" />
                          {selectedDocument?.filename || ''}
                        </>
                      )}
                    </h4>
                    <p className="text-muted mb-2">
                      {isCreatingTemplate 
                        ? 'Define requirements for documents agents need to submit'
                        : `AI Analysis Review & Enhancement • Last updated: ${selectedDocument?.updated_at ? new Date(selectedDocument.updated_at).toLocaleDateString() : ''}`}
                    </p>
                    {/* Display transaction types in header */}
                    {editedAnalysis?.document_classification?.transaction_types?.length > 0 && (
                      <div className="d-flex gap-2 flex-wrap">
                        <small className="text-muted me-2">Applicable to:</small>
                        {editedAnalysis.document_classification.transaction_types.map((type: string) => (
                          <Badge key={type} bg="info" className="text-capitalize">
                            {type}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    {selectedDocument?.s3_url && !isCreatingTemplate && (
                      <>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => {
                            if (selectedDocument.s3_url) {
                              // Create a download link
                              const link = document.createElement('a');
                              link.href = selectedDocument.s3_url;
                              link.download = selectedDocument.filename;
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faDownload} className="me-1" />
                          Download
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => {
                            if (selectedDocument.s3_url) {
                              window.open(selectedDocument.s3_url, '_blank');
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                          Open in New Tab
                        </Button>
                      </>
                    )}
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={saveAnalysis}
                      disabled={saving}
                    >
                      {saving ? (
                        <Spinner animation="border" size="sm" className="me-1" />
                      ) : (
                        <FontAwesomeIcon icon={faSave} className="me-1" />
                      )}
                      Save Analysis
                    </Button>
                  </div>
                </div>
              </div>

              {/* Alerts */}
              {error && (
                <Alert variant="danger" className="m-3" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" className="m-3" dismissible onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              {/* Analysis Editor */}
              <div className="flex-grow-1 overflow-auto p-4">
                <Tab.Container activeKey={activeTabKey} onSelect={(key) => setActiveTabKey(key || 'summary')}>
                  <Nav variant="pills" className="mb-4 ai-editor-tabs">
                    <Nav.Item>
                      <Nav.Link eventKey="summary">
                        <FontAwesomeIcon icon={faRobot} className="me-1" />
                        Summary & Purpose
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="key-info">
                        <FontAwesomeIcon icon={faKey} className="me-1" />
                        Key Information
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="usage">
                        <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                        Usage Context
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="compliance">
                        <FontAwesomeIcon icon={faClipboardCheck} className="me-1" />
                        Compliance
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="classification">
                        <FontAwesomeIcon icon={faFilter} className="me-1" />
                        Classification
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="qa-keywords">
                        <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                        Q&A & Keywords
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  <Tab.Content>
                    {/* Summary Tab */}
                    <Tab.Pane eventKey="summary">
                      <Card className="mb-3">
                        <Card.Body>
                          <h5 className="mb-3">Document Summary</h5>
                          {isCreatingTemplate && (
                            <Form.Group className="mb-3">
                              <Form.Label>Template Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={editedAnalysis?.filename || ''}
                                onChange={(e) => handleAnalysisChange('filename', e.target.value)}
                                placeholder="e.g., Earnest Money Deposit Receipt"
                              />
                              <Form.Text className="text-muted">
                                Enter a clear name for this document requirement
                              </Form.Text>
                            </Form.Group>
                          )}
                          <Form.Group className="mb-3">
                            <Form.Label>{isCreatingTemplate ? 'Requirement Description' : 'AI Generated Summary'}</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              value={editedAnalysis?.summary || ''}
                              onChange={(e) => handleAnalysisChange('summary', e.target.value)}
                              placeholder={isCreatingTemplate 
                                ? "Describe what this document is and why it's required..." 
                                : "AI summary of the document..."}
                            />
                            <Form.Text className="text-muted">
                              Provide a concise summary of what this document is and its primary purpose
                            </Form.Text>
                          </Form.Group>

                          <Form.Group>
                            <Form.Label>Document Purpose</Form.Label>
                            <Form.Control
                              type="text"
                              value={editedAnalysis?.document_purpose || ''}
                              onChange={(e) => handleAnalysisChange('document_purpose', e.target.value)}
                              placeholder="e.g., Establishes agency relationship with buyer"
                            />
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    {/* Key Information Tab */}
                    <Tab.Pane eventKey="key-info">
                      <Card>
                        <Card.Body>
                          <h5 className="mb-3">Key Information</h5>
                          
                          {/* Transaction Types - moved from Classification tab for better visibility */}
                          <Form.Group className="mb-4">
                            <Form.Label>
                              <FontAwesomeIcon icon={faBuilding} className="me-1" />
                              Transaction Types
                              <span className="text-danger ms-1">*</span>
                            </Form.Label>
                            <Alert variant="info" className="mb-2 py-2">
                              <small>
                                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                Select which transaction types require this document. This helps filter documents for specific workflows (e.g., rental vs purchase).
                              </small>
                            </Alert>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              {['Purchase', 'Listing', 'Rental', 'Referral', 'Land Contract', 'Commercial'].map(transactionType => {
                                const currentTypes = editedAnalysis?.document_classification?.transaction_types || [];
                                const isSelected = currentTypes.includes(transactionType);
                                
                                return (
                                  <Button
                                    key={transactionType}
                                    variant={isSelected ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => {
                                      const newTypes = isSelected
                                        ? currentTypes.filter(t => t !== transactionType)
                                        : [...currentTypes, transactionType];
                                      handleAnalysisChange('document_classification.transaction_types', newTypes);
                                    }}
                                    title={
                                      transactionType === 'Purchase' ? 'Buyer representation transactions' :
                                      transactionType === 'Listing' ? 'Seller representation transactions' :
                                      transactionType === 'Rental' ? 'Landlord/Tenant lease transactions' :
                                      transactionType === 'Referral' ? 'Agent-to-agent referral transactions' :
                                      transactionType === 'Land Contract' ? 'Vendor/Vendee financing transactions' :
                                      transactionType === 'Commercial' ? 'Commercial property transactions' : ''
                                    }
                                  >
                                    {isSelected && <FontAwesomeIcon icon={faCheckCircle} className="me-1" />}
                                    {transactionType}
                                  </Button>
                                );
                              })}
                            </div>
                            <Form.Text className="text-muted">
                              Select all transaction types where this document applies
                            </Form.Text>
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Parties Involved</Form.Label>
                            
                            {/* Show transaction type context if any are selected */}
                            {editedAnalysis?.document_classification?.transaction_types?.length > 0 ? (
                              <Alert variant="info" className="mb-2 py-2">
                                <small>Showing parties for: <strong>{editedAnalysis.document_classification.transaction_types.join(', ')}</strong> transactions</small>
                              </Alert>
                            ) : (
                              <Alert variant="warning" className="mb-2 py-2">
                                <small>💡 <strong>Tip:</strong> Select transaction types above to see relevant parties for each type (e.g., Landlord/Tenant for rentals)</small>
                              </Alert>
                            )}
                            
                            <div className="mb-2">
                              <div className="d-flex flex-wrap gap-2">
                                {(() => {
                                  // Get selected transaction types
                                  const selectedTypes = editedAnalysis?.document_classification?.transaction_types || [];
                                  
                                  // Define parties by transaction type
                                  const partiesByType: { [key: string]: string[] } = {
                                    'Purchase': ['Buyer', 'Seller', 'Buyer Agent', 'Seller Agent', 'Brokerage', 'Escrow', 'Title Company', 'Lender', 'HOA'],
                                    'Listing': ['Seller', 'Seller Agent', 'Brokerage', 'Buyer', 'Buyer Agent'],
                                    'Rental': ['Landlord', 'Tenant', 'Landlord Agent', 'Tenant Agent', 'Property Manager', 'Brokerage'],
                                    'Referral': ['Referring Agent', 'Receiving Agent', 'Referring Brokerage', 'Receiving Brokerage', 'Client'],
                                    'Land Contract': ['Vendor', 'Vendee', 'Vendor Agent', 'Vendee Agent', 'Brokerage', 'Title Company'],
                                    'Commercial': ['Buyer/Tenant', 'Seller/Landlord', 'Buyer/Tenant Agent', 'Seller/Landlord Agent', 'Brokerage', 'Attorney', 'Title Company']
                                  };
                                  
                                  // Get unique parties based on selected transaction types
                                  let relevantParties = new Set<string>();
                                  
                                  // Always include common parties
                                  ['Brokerage', 'Attorney', 'Title Company'].forEach(party => relevantParties.add(party));
                                  
                                  // Add type-specific parties
                                  selectedTypes.forEach(type => {
                                    const parties = partiesByType[type] || [];
                                    parties.forEach(party => relevantParties.add(party));
                                  });
                                  
                                  // If no types selected, show all common parties
                                  if (selectedTypes.length === 0) {
                                    relevantParties = new Set(['Buyer', 'Seller', 'Buyer Agent', 'Seller Agent', 'Landlord', 'Tenant', 'Landlord Agent', 'Tenant Agent', 'Brokerage', 'Escrow', 'Title Company', 'Lender', 'HOA', 'Attorney', 'Property Manager']);
                                  }
                                  
                                  // Convert to sorted array
                                  const partiesList = Array.from(relevantParties).sort();
                                  
                                  return partiesList.map(party => {
                                    const currentParties = editedAnalysis?.key_information?.parties_involved || [];
                                    const isSelected = currentParties.some(p => p.toLowerCase() === party.toLowerCase());
                                    
                                    return (
                                      <Button
                                        key={party}
                                        variant={isSelected ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => {
                                          const newParties = isSelected
                                            ? currentParties.filter(p => p.toLowerCase() !== party.toLowerCase())
                                            : [...currentParties, party];
                                          handleAnalysisChange('key_information.parties_involved', newParties);
                                        }}
                                      >
                                        {isSelected && <FontAwesomeIcon icon={faCheckCircle} className="me-1" />}
                                        {party}
                                      </Button>
                                    );
                                  });
                                })()}
                              </div>
                              <Form.Text className="text-muted">
                                Select all parties involved in this document
                              </Form.Text>
                            </div>
                            
                            {/* Show any custom parties that don't match our predefined list */}
                            {(editedAnalysis?.key_information?.parties_involved?.filter(
                              party => !['Buyer', 'Seller', 'Buyer Agent', 'Seller Agent', 'Landlord', 'Tenant', 'Landlord Agent', 'Tenant Agent', 'Brokerage', 'Escrow', 'Title Company', 'Lender', 'HOA'].some(
                                p => p.toLowerCase() === party.toLowerCase()
                              )
                            ) || []).length > 0 && (
                              <Alert variant="info" className="mt-2 small">
                                <strong>Other parties:</strong> {editedAnalysis?.key_information?.parties_involved?.filter(
                                  party => !['Buyer', 'Seller', 'Buyer Agent', 'Seller Agent', 'Landlord', 'Tenant', 'Landlord Agent', 'Tenant Agent', 'Brokerage', 'Escrow', 'Title Company', 'Lender', 'HOA'].some(
                                    p => p.toLowerCase() === party.toLowerCase()
                                  )
                                ).join(', ')}
                              </Alert>
                            )}
                          </Form.Group>

                          <Form.Group>
                            <Form.Label>Critical Terms</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={editedAnalysis?.key_information?.critical_terms?.join('\n') || ''}
                              onChange={(e) => handleAnalysisChange('key_information.critical_terms', 
                                e.target.value.split('\n')
                              )}
                              onBlur={(e) => handleAnalysisChange('key_information.critical_terms',
                                e.target.value.split('\n').filter(s => s.trim())
                              )}
                              placeholder="Enter critical terms (one per line):&#10;• Claims history period&#10;• Types of claims&#10;• Coverage amounts"
                            />
                            <Form.Text className="text-muted">
                              Key provisions, coverage details, or important conditions in this document
                            </Form.Text>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    {/* Usage Context Tab */}
                    <Tab.Pane eventKey="usage">
                      <Card className="mb-3">
                        <Card.Body>
                          <h5 className="mb-3">When Required</h5>
                          
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="checkbox"
                              label="Always Required (regardless of conditions)"
                              checked={editedAnalysis?.usage_context?.when_required?.always_required || false}
                              onChange={(e) => handleAnalysisChange('usage_context.when_required.always_required', e.target.checked)}
                              className="mb-2"
                            />
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Transaction Stages</Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                              {['pre_contract', 'showing', 'under_contract', 'closing', 'cancelled'].map(stage => {
                                const stageLabel = stage.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                                const isSelected = editedAnalysis?.usage_context?.when_required?.transaction_stages?.includes(stage);
                                
                                return (
                                  <Button
                                    key={stage}
                                    variant={isSelected ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => {
                                      const currentStages = editedAnalysis?.usage_context?.when_required?.transaction_stages || [];
                                      const newStages = isSelected
                                        ? currentStages.filter(s => s !== stage)
                                        : [...currentStages, stage];
                                      handleAnalysisChange('usage_context.when_required.transaction_stages', newStages);
                                    }}
                                  >
                                    {isSelected && <FontAwesomeIcon icon={faCheckCircle} className="me-1" />}
                                    {stageLabel}
                                  </Button>
                                );
                              })}
                            </div>
                            <Form.Text className="text-muted">
                              Select the transaction stages where this document is required
                            </Form.Text>
                            
                            {/* Show legacy text values if any exist that don't match our stages */}
                            {(editedAnalysis?.usage_context?.when_required?.transaction_stages?.filter(
                              stage => !['pre_contract', 'showing', 'under_contract', 'closing', 'cancelled'].includes(stage)
                            ) || []).length > 0 && (
                              <Alert variant="info" className="mt-2 small">
                                <strong>Other stages:</strong> {editedAnalysis?.usage_context?.when_required?.transaction_stages?.filter(
                                  stage => !['pre_contract', 'showing', 'under_contract', 'closing', 'cancelled'].includes(stage)
                                ).join(', ')}
                              </Alert>
                            )}
                          </Form.Group>

                          <div className="mb-3">
                            <label className="form-label">
                              <FontAwesomeIcon icon={faClock} className="me-1" />
                              Timing Conditions 
                              <small className="text-muted ms-2">(for Agent Kairos automation)</small>
                            </label>
                            {editedAnalysis?.usage_context?.when_required?.timing_conditions?.map((condition, index) => (
                              <Card key={index} className="mb-3" style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
                                <Card.Body className="p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h6 className="mb-0">Condition {index + 1}</h6>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => {
                                        const conditions = editedAnalysis?.usage_context?.when_required?.timing_conditions?.filter((_, i) => i !== index) || [];
                                        handleAnalysisChange('usage_context.when_required.timing_conditions', conditions);
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faExclamationTriangle} /> Remove
                                    </Button>
                                  </div>
                                  
                                  <Row className="g-2">
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label size="sm">Trigger Event</Form.Label>
                                        <Form.Select
                                          size="sm"
                                          value={condition.trigger || ''}
                                          onChange={(e) => {
                                            const conditions = [...(editedAnalysis?.usage_context?.when_required?.timing_conditions || [])];
                                            conditions[index] = { ...conditions[index], trigger: e.target.value };
                                            handleAnalysisChange('usage_context.when_required.timing_conditions', conditions);
                                          }}
                                        >
                                          <option value="">Select trigger...</option>
                                          <optgroup label="Contract">
                                            <option value="upon_contract_acceptance">Upon Contract Acceptance</option>
                                            <option value="upon_offer_submission">Upon Offer Submission</option>
                                            <option value="upon_contract_execution">Upon Contract Execution</option>
                                            <option value="upon_mutual_acceptance">Upon Mutual Acceptance</option>
                                          </optgroup>
                                          <optgroup label="Inspection">
                                            <option value="upon_inspection_completion">Upon Inspection Completion</option>
                                            <option value="upon_inspection_request">Upon Inspection Request</option>
                                            <option value="upon_appraisal_order">Upon Appraisal Order</option>
                                            <option value="upon_appraisal_receipt">Upon Appraisal Receipt</option>
                                          </optgroup>
                                          <optgroup label="Financing">
                                            <option value="upon_loan_application">Upon Loan Application</option>
                                            <option value="upon_loan_approval">Upon Loan Approval</option>
                                            <option value="upon_financing_contingency_removal">Upon Financing Contingency Removal</option>
                                            <option value="upon_earnest_money_deposit">Upon Earnest Money Deposit</option>
                                          </optgroup>
                                          <optgroup label="Closing">
                                            <option value="prior_to_closing">Prior to Closing</option>
                                            <option value="at_closing">At Closing</option>
                                            <option value="after_closing">After Closing</option>
                                            <option value="upon_title_clearance">Upon Title Clearance</option>
                                          </optgroup>
                                          <optgroup label="Disclosure">
                                            <option value="upon_listing">Upon Listing</option>
                                            <option value="before_showing">Before Showing</option>
                                            <option value="with_purchase_agreement">With Purchase Agreement</option>
                                            <option value="upon_material_change">Upon Material Change</option>
                                          </optgroup>
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                    
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label size="sm">Deadline</Form.Label>
                                        <Form.Select
                                          size="sm"
                                          value={condition.deadline || ''}
                                          onChange={(e) => {
                                            const conditions = [...(editedAnalysis?.usage_context?.when_required?.timing_conditions || [])];
                                            conditions[index] = { ...conditions[index], deadline: e.target.value };
                                            handleAnalysisChange('usage_context.when_required.timing_conditions', conditions);
                                          }}
                                        >
                                          <option value="">Select deadline...</option>
                                          <option value="immediately">Immediately</option>
                                          <option value="within_24_hours">Within 24 hours</option>
                                          <optgroup label="Business Days (M-F)">
                                            <option value="within_2_business_days">Within 2 business days</option>
                                            <option value="within_3_business_days">Within 3 business days</option>
                                            <option value="within_5_business_days">Within 5 business days</option>
                                          </optgroup>
                                          <optgroup label="Calendar Days">
                                            <option value="within_3_days">Within 3 calendar days</option>
                                            <option value="within_5_days">Within 5 calendar days</option>
                                            <option value="within_7_days">Within 7 calendar days</option>
                                            <option value="within_10_days">Within 10 calendar days</option>
                                            <option value="within_14_days">Within 14 calendar days</option>
                                            <option value="within_17_days">Within 17 calendar days</option>
                                            <option value="within_21_days">Within 21 calendar days</option>
                                            <option value="within_30_days">Within 30 calendar days</option>
                                          </optgroup>
                                          <optgroup label="Relative to Closing">
                                            <option value="prior_to_closing">Prior to closing</option>
                                            <option value="at_closing">At closing</option>
                                          </optgroup>
                                          <option value="as_specified">As specified in contract</option>
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                    
                                    <Col md={12}>
                                      <Form.Group>
                                        <Form.Label size="sm">Priority Level</Form.Label>
                                        <div className="d-flex gap-2">
                                          {(['critical', 'high', 'medium', 'low'] as const).map(priority => {
                                            const isSelected = (condition.priority || 'medium') === priority;
                                            const priorityColors: Record<string, string> = {
                                              critical: '#dc2626',
                                              high: '#f59e0b',
                                              medium: '#3b82f6',
                                              low: '#6b7280'
                                            };
                                            
                                            return (
                                              <Button
                                                key={priority}
                                                size="sm"
                                                variant={isSelected ? 'primary' : 'outline-secondary'}
                                                style={isSelected ? { backgroundColor: priorityColors[priority], borderColor: priorityColors[priority] } : {}}
                                                onClick={() => {
                                                  const conditions = [...(editedAnalysis?.usage_context?.when_required?.timing_conditions || [])];
                                                  conditions[index] = { ...conditions[index], priority };
                                                  handleAnalysisChange('usage_context.when_required.timing_conditions', conditions);
                                                }}
                                              >
                                                {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                              </Button>
                                            );
                                          })}
                                        </div>
                                        <Form.Text className="text-muted">
                                          Critical = Legal requirement | High = Contract compliance | Medium = Best practice | Low = Recommended
                                        </Form.Text>
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                  
                                  {/* Agent Kairos Integration Preview */}
                                  {condition.trigger && condition.deadline && (
                                    <Alert variant="info" className="mt-3 mb-0">
                                      <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faRobot} className="me-2" size="lg" />
                                        <div>
                                          <strong style={{ fontSize: '1rem' }}>Agent Kairos will monitor:</strong>{' '}
                                          <span style={{ fontSize: '1rem' }}>
                                            {condition.deadline.replace(/_/g, ' ')} from {condition.trigger.replace(/_/g, ' ')}
                                          </span>
                                          {condition.deadline.includes('business') && (
                                            <span className="text-muted ms-2" style={{ fontSize: '0.9rem' }}>
                                              <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                              (Excludes weekends)
                                            </span>
                                          )}
                                          {condition.priority === 'critical' && (
                                            <span className="ms-2 text-danger" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                              (Legal Requirement)
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </Alert>
                                  )}
                                </Card.Body>
                              </Card>
                            ))}
                            
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => {
                                    const conditions = [...(editedAnalysis?.usage_context?.when_required?.timing_conditions || [])];
                                    conditions.push({ trigger: '', deadline: '', priority: 'medium' });
                                    handleAnalysisChange('usage_context.when_required.timing_conditions', conditions);
                                  }}
                                  className="me-2"
                                >
                                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                                  Add Timing Condition
                                </Button>
                                
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => setShowTimingTemplates(true)}
                                >
                                  <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                                  Use Template
                                </Button>
                              </div>
                              
                              {isCreatingTemplate && editedAnalysis?.filename?.toLowerCase().includes('earnest') && (
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => {
                                    // Apply earnest money template
                                    handleAnalysisChange('usage_context.when_required.timing_conditions', [{
                                      trigger: 'upon_contract_acceptance',
                                      deadline: 'within_2_business_days',
                                      priority: 'critical'
                                    }]);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                                  Use Earnest Money Template
                                </Button>
                              )}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>

                      <Card>
                        <Card.Body>
                          <h5 className="mb-3">Why Important</h5>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Legal Requirements</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={editedAnalysis?.usage_context?.why_important?.legal_requirements?.join('\n') || ''}
                              onChange={(e) => handleAnalysisChange('usage_context.why_important.legal_requirements', 
                                e.target.value.split('\n').filter(s => s.trim())
                              )}
                              placeholder="One requirement per line..."
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Protections Provided</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={editedAnalysis?.usage_context?.why_important?.protections_provided?.join('\n') || ''}
                              onChange={(e) => handleAnalysisChange('usage_context.why_important.protections_provided', 
                                e.target.value.split('\n').filter(s => s.trim())
                              )}
                              placeholder="One protection per line..."
                            />
                          </Form.Group>

                          <Form.Group>
                            <Form.Label>Risks if Missing</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={editedAnalysis?.usage_context?.why_important?.risks_if_missing?.join('\n') || ''}
                              onChange={(e) => handleAnalysisChange('usage_context.why_important.risks_if_missing', 
                                e.target.value.split('\n').filter(s => s.trim())
                              )}
                              placeholder="One risk per line..."
                            />
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    {/* Compliance Tab */}
                    <Tab.Pane eventKey="compliance">
                      <Card>
                        <Card.Body>
                          <h5 className="mb-3">Compliance Requirements</h5>
                          
                          <Form.Group>
                            <Form.Label>Legal References</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={editedAnalysis?.compliance_requirements?.legal_references?.join('\n') || ''}
                              onChange={(e) => handleAnalysisChange('compliance_requirements.legal_references', 
                                e.target.value.split('\n').filter(s => s.trim())
                              )}
                              placeholder="One legal reference per line..."
                            />
                            <Form.Text className="text-muted">
                              Include relevant statutes, regulations, or legal codes
                            </Form.Text>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    {/* Classification Tab */}
                    <Tab.Pane eventKey="classification">
                      <Card>
                        <Card.Body>
                          <h5 className="mb-3">Document Classification</h5>
                          
                          {/* Display current transaction types at the top */}
                          {editedAnalysis?.document_classification?.transaction_types?.length > 0 && (
                            <Alert variant="info" className="mb-3">
                              <small className="text-muted d-block mb-1">Currently applicable for:</small>
                              <div className="d-flex gap-2 flex-wrap">
                                {editedAnalysis.document_classification.transaction_types.map((type: string) => (
                                  <Badge key={type} bg="primary" className="text-capitalize">
                                    {type} Transactions
                                  </Badge>
                                ))}
                              </div>
                            </Alert>
                          )}
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Primary Category</Form.Label>
                            <Form.Select
                              value={editedAnalysis?.document_classification?.primary_category || ''}
                              onChange={(e) => handleAnalysisChange('document_classification.primary_category', e.target.value)}
                            >
                              <option value="">Select category...</option>
                              <option value="required">Required</option>
                              <option value="conditional">Conditional</option>
                              <option value="optional">Optional</option>
                              <option value="supplemental">Supplemental</option>
                            </Form.Select>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Subcategory</Form.Label>
                            <Form.Select
                              value={editedAnalysis?.document_classification?.subcategory || ''}
                              onChange={(e) => handleAnalysisChange('document_classification.subcategory', e.target.value)}
                            >
                              <option value="">Select subcategory...</option>
                              <option value="agency">Agency Documents</option>
                              <option value="disclosure">Disclosures</option>
                              <option value="financial">Financial Documents</option>
                              <option value="inspection">Inspection Reports</option>
                              <option value="title">Title Documents</option>
                              <option value="contract">Contracts & Agreements</option>
                              <option value="addendum">Addendums & Amendments</option>
                              <option value="contingency">Contingency Documents</option>
                              <option value="closing">Closing Documents</option>
                              <option value="compliance">Compliance & Legal</option>
                              <option value="insurance">Insurance Documents</option>
                              <option value="hoa">HOA Documents</option>
                              <option value="other">Other</option>
                            </Form.Select>
                          </Form.Group>

                          {/* Transaction Types - Moved to Key Information tab */}
                          <Alert variant="info" className="mb-3">
                            <small>
                              <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                              <strong>Transaction Types</strong> have been moved to the <strong>Key Information</strong> tab for better visibility and workflow integration.
                              {editedAnalysis?.document_classification?.transaction_types?.length > 0 && (
                                <>
                                  <br />
                                  Currently selected: <strong>{editedAnalysis.document_classification.transaction_types.join(', ')}</strong>
                                </>
                              )}
                            </small>
                          </Alert>

                          <Form.Group>
                            <Form.Label>Required Frequency</Form.Label>
                            <Form.Select
                              value={editedAnalysis?.document_classification?.required_frequency || ''}
                              onChange={(e) => handleAnalysisChange('document_classification.required_frequency', e.target.value)}
                            >
                              <option value="">Select frequency...</option>
                              <option value="once_per_transaction">Once Per Transaction</option>
                              <option value="per_party">Per Party</option>
                              <option value="per_property">Per Property</option>
                              <option value="annual">Annual</option>
                              <option value="as_needed">As Needed</option>
                            </Form.Select>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    {/* Q&A and Keywords Tab */}
                    <Tab.Pane eventKey="qa-keywords">
                      <Card className="mb-3">
                        <Card.Body>
                          <h5 className="mb-3">Chatbot Q&A</h5>
                          {editedAnalysis?.chatbot_qa?.map((qa, index) => (
                            <div key={index} className="border rounded p-3 mb-2">
                              <Form.Group className="mb-2">
                                <Form.Label>Question {index + 1}</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={qa.question}
                                  onChange={(e) => {
                                    const newQA = [...(editedAnalysis?.chatbot_qa || [])];
                                    newQA[index] = { ...newQA[index], question: e.target.value };
                                    handleAnalysisChange('chatbot_qa', newQA);
                                  }}
                                  placeholder="Enter question..."
                                />
                              </Form.Group>
                              <Form.Group>
                                <Form.Label>Answer</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={qa.answer}
                                  onChange={(e) => {
                                    const newQA = [...(editedAnalysis?.chatbot_qa || [])];
                                    newQA[index] = { ...newQA[index], answer: e.target.value };
                                    handleAnalysisChange('chatbot_qa', newQA);
                                  }}
                                  placeholder="Enter answer..."
                                />
                              </Form.Group>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="mt-2"
                                onClick={() => {
                                  const newQA = editedAnalysis?.chatbot_qa?.filter((_, i) => i !== index) || [];
                                  handleAnalysisChange('chatbot_qa', newQA);
                                }}
                              >
                                Remove Q&A
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              const newQA = [...(editedAnalysis?.chatbot_qa || [])];
                              newQA.push({ question: '', answer: '' });
                              handleAnalysisChange('chatbot_qa', newQA);
                            }}
                          >
                            Add Q&A Pair
                          </Button>
                        </Card.Body>
                      </Card>

                      <Card className="mb-3">
                        <Card.Body>
                          <h5 className="mb-3">Search Keywords</h5>
                          <Form.Group>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={editedAnalysis?.search_keywords?.join('\n') || ''}
                              onChange={(e) => handleAnalysisChange('search_keywords', 
                                e.target.value.split('\n').filter(s => s.trim())
                              )}
                              placeholder="Enter one keyword per line..."
                            />
                            <Form.Text className="text-muted">
                              Enter search keywords that help users find this document
                            </Form.Text>
                          </Form.Group>
                        </Card.Body>
                      </Card>

                      <Card>
                        <Card.Body>
                          <h5 className="mb-3">Red Flags</h5>
                          <Form.Group>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={editedAnalysis?.analysis_details?.red_flags?.join('\n') || ''}
                              onChange={(e) => handleAnalysisChange('analysis_details.red_flags', 
                                e.target.value.split('\n').filter(s => s.trim())
                              )}
                              placeholder="Enter one red flag per line..."
                            />
                            <Form.Text className="text-muted">
                              List any concerns or issues identified in this document
                            </Form.Text>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </div>
          ) : (
            <div className="h-100 d-flex align-items-center justify-content-center">
              <div className="text-center text-muted">
                <img 
                  src={KairosLogo} 
                  alt="Kairos AI" 
                  className="mb-3"
                  style={{ 
                    width: '80px', 
                    height: '80px',
                    opacity: 0.5
                  }}
                />
                <h5>Select a document to review AI analysis</h5>
                <p>Choose from the documents on the left to view and edit AI-generated insights</p>
              </div>
            </div>
          )}
        </Col>
      </Row>
      )}

      {/* Compliance Rules Tab Content */}
      {activeTab === 'rules' && (
        <div className="p-4">
          <DocumentComplianceRulesSimplified 
            brokerageId={effectiveBrokerageId}
          />
        </div>
      )}

      {/* Timing Templates Modal */}
      <Modal
        show={showTimingTemplates}
        onHide={() => setShowTimingTemplates(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faLightbulb} className="me-2 text-warning" />
            Select Timing Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DocumentTimingTemplates
            documentType={editedAnalysis?.filename || editedAnalysis?.document_type}
            onSelectTemplate={(template) => {
              // Apply the selected template
              const conditions = [...(editedAnalysis?.usage_context?.when_required?.timing_conditions || [])];
              conditions.push({
                trigger: template.trigger,
                deadline: template.deadline,
                priority: template.priority
              });
              handleAnalysisChange('usage_context.when_required.timing_conditions', conditions);
              setShowTimingTemplates(false);
              toast.success(`Applied ${template.name} timing template`);
            }}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AIDocumentReview;